<template>
  <main
    v-if="content && copy"
    class="text-white">
    <div class="bg-gray-500 pb-12 lg:pb-24">
      <JobHeader />

      <Container class="flex flex-wrap items-start justify-between space-x-6 lg:flex-nowrap">
        <div class="">
          <h1 class="max-w-2xl whitespace-pre-line text-5xl tracking-tight text-white lg:text-7xl lg:leading-tight">
            {{ content.title }}
          </h1>

          <p
            class="mt-6 max-w-2xl whitespace-pre-line font-normal leading-snug text-gray-100 lg:text-xl lg:leading-snug">
            {{ content.description }}
          </p>

          <JobSidebar
            :division="content.division"
            :employment-type="content.employmentType"
            :work-types="content.workTypes"
            :content="{
              locations: { title: copy.sidebar.locations, items: content.locations },
              recruiter: { title: copy.sidebar.recruiter, id: content.recruiter },
              employers: { title: copy.sidebar.employer, items: content.employers },
              deadline: { title: copy.sidebar.deadline, copy: content.deadline }
            }"
            :cta="copy.sidebar.cta"
            class="mt-12 block flex-shrink-0 lg:hidden"
            @button-click="handleClick" />

          <JobContent
            :content="{
              responsibilities: {
                title: copy.main.responsibilities,
                titleChinese: copy.main.responsibilitiesChinese,
                copy: content.responsibilities
              },
              skills: {
                title: copy.main.skills,
                titleChinese: copy.main.skillsChinese,
                copy: content.skills
              },
              experience: {
                title: copy.main.experience,
                titleChinese: copy.main.experienceChinese,
                copy: content.experience
              },
              offer: {
                title: copy.main.offer,
                titleChinese: copy.main.offerChinese,
                copy: content.offer
              },
              compensation: {
                title: copy.main.compensation,
                titleChinese: copy.main.compensationChinese,
                copy: content.compensation
              }
            }"
            :si="content.employers.includes(Employer.EKIPA2) ? { title: copy.main.si, content: content.si } : null"
            :privacy-type="content.privacyType"
            class="" />
        </div>

        <JobSidebar
          :division="content.division"
          :employment-type="content.employmentType"
          :work-types="content.workTypes"
          :content="{
            locations: { title: copy.sidebar.locations, items: content.locations },
            recruiter: { title: copy.sidebar.recruiter, id: content.recruiter },
            employers: { title: copy.sidebar.employer, items: content.employers },
            deadline: { title: copy.sidebar.deadline, copy: content.deadline }
          }"
          :cta="copy.sidebar.cta"
          class="top-24 hidden flex-shrink-0 lg:sticky lg:block"
          @button-click="handleClick" />
      </Container>
    </div>

    <JobModalInternalReferral
      :id="entryId"
      :is-open="isModalOpen"
      :title="content.title"
      :tagline="copy.modal.tagline"
      :step1="copy.modal.step1"
      :step2="copy.modal.step2"
      :success="copy.modal.success"
      :error="copy.modal.error"
      @toggle="setIsOpen" />
  </main>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'
  import { useRoute } from 'vue-router'

  import { Employer } from '~/constants/jobs'
  import routesList from '~/content/routes.json'
  import { stripEndingSlash } from '~/utils/urls'

  const { $event, $lenisMount } = useNuxtApp()
  const route = useRoute()
  const generalData = await useAsyncData('jobs-general', () => queryContent('globals', 'jobs').findOne())
  const isModalOpen = ref(false)

  const searchUri = stripEndingSlash(route.path).replace('/internal-job-referrals/', '/jobs/')
  const page = routesList.find(item => item.url === searchUri)

  let entryId = ''

  if (!page) {
    throw createError({
      statusCode: 404,
      statusMessage: 'Page Not Found',
      fatal: true
    })
  } else {
    entryId = page.id
  }

  const { data } = await useAsyncData(entryId, () => queryContent('jobs').where({ id: entryId }).findOne())

  const content = computed(() => {
    if (data && data.value) {
      return data.value
    }

    return false
  })

  const copy = computed(() => {
    if (generalData.data && generalData.data.value) {
      return generalData.data.value
    }

    return false
  })

  definePageMeta({
    layout: 'internal',
    middleware: ['internal-job-referrals']
  })

  useHead({
    title: data.value?.meta?.title,
    meta: [
      { hid: 'description', name: 'description', content: data.value?.meta?.description },

      { hid: 'og:title', property: 'og:title', content: data.value?.social?.title },
      // { hid: 'og:url', property: 'og:url', content: this.pageUrl },
      { hid: 'og:description', property: 'og:description', content: data.value?.social?.description },
      { hid: 'og:image', property: 'og:image', content: data.value?.social?.image },

      { hid: 'twitter:title', name: 'twitter:title', content: data.value?.social?.title },
      // { hid: 'twitter:url', name: 'twitter:url', content: this.pageUrl },
      { hid: 'twitter:description', name: 'twitter:description', content: data.value?.meta?.description },
      { hid: 'twitter:image', name: 'twitter:image', content: data.value?.social?.image },

      { hid: 'robots', name: 'robots', content: 'noindex' }
    ]
  })

  onMounted(() => {
    $lenisMount()
  })

  function setIsOpen(state: boolean) {
    isModalOpen.value = state
  }

  function handleClick() {
    setIsOpen(true)

    if (!content.value || !content.value.title) {
      return
    }
    $event('Job Application', { props: { 'Step 1 (internal)': content.value.title } })
  }
</script>
